import { silently, track } from "./analytics";
import type { AuthMethod } from "./events";

const PENDING_LOGIN_KEY = "tender-app:analytics-pending-login";
const PENDING_LOGIN_MAX_AGE_MS = 15 * 60 * 1000;
const AUTH_METHODS: ReadonlyArray<string> = ["email", "google", "microsoft"];

const isAuthMethod = (value: unknown): value is AuthMethod =>
  typeof value === "string" && AUTH_METHODS.includes(value);

/**
 * A login completes before the app knows who the user is or which company they
 * work in, so an event sent there would belong to no account. The login is
 * remembered here and sent by `flushPendingLogin` once the company is synced.
 */
export const rememberLogin = (method: AuthMethod): void =>
  silently(() => {
    sessionStorage.setItem(
      PENDING_LOGIN_KEY,
      JSON.stringify({ method, completedAt: Date.now() }),
    );
  });

/** The method of a remembered login recent enough to still be this one. */
const recentLoginMethod = (stored: string): AuthMethod | undefined => {
  const login: unknown = JSON.parse(stored);
  if (typeof login !== "object" || login === null) return undefined;

  const { method, completedAt } = login as Record<string, unknown>;
  if (!isAuthMethod(method) || typeof completedAt !== "number") {
    return undefined;
  }

  const age = Date.now() - completedAt;
  return age >= 0 && age <= PENDING_LOGIN_MAX_AGE_MS ? method : undefined;
};

/**
 * Sends a remembered login once, after the user and their company are set, so
 * the event carries the account. Removed before sending: a login that could
 * not be removed would otherwise go out on every navigation.
 */
export const flushPendingLogin = (): void =>
  silently(() => {
    const stored = sessionStorage.getItem(PENDING_LOGIN_KEY);
    if (stored === null) return;
    sessionStorage.removeItem(PENDING_LOGIN_KEY);

    const method = recentLoginMethod(stored);
    if (method) track("auth.login.completed", { method });
  });
